/**
 * @file useCreateReview.js
 */

import { useMutation } from "@apollo/client";
import { CREATE_REVIEW } from "cache/Review/mutations"; // Update the path as per your project structure

/**
 * @hook useCreateReview
 *
 * @description
 * Custom hook that handles the functionality to create a new review in the Review collection.
 * This hook is typically used in components that require the functionality to add a new review.
 *
 * @returns
 * - createReview: Method to execute the createReview mutation
 * - loading: Boolean indicating the loading state
 * - error: Error object if there's an error
 */
const useCreateReview = () => {

	// Initialization of the Apollo Client mutation hook
	const [createReviewMutation, { loading, error }] = useMutation(CREATE_REVIEW);

	/**
	 * Custom mutation to create a review.
	 *
	 * @param {Object} input - The input object containing the review data.
	 * @returns {Object} - An object containing a boolean indicating the success of the review creation.
	 * @throws {Error} - Throws an error if the mutation execution fails.
	 */
	const createReview = async (input) => {
		try {
			const { data } = await createReviewMutation({
				variables: {
					input
				}
			});
			if (error) {
				throw new Error(error);
			}
			// Process the returned data as required
			return { data: data.createReview };
		} catch (error) {
			return { error };
		}
	};

	return { createReview, loading, error };

};

export default useCreateReview;
